import { useEffect, useState } from 'react';
import { Icon } from '@/components/Icon';
import { Spinner } from '@/components/Spinner';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { showToast } from '@/lib/toast';
import { deleteRole, fetchRole, type PermissionGroup, type RoleRow } from '@/api/roles';
import { TileMenu } from '@/components/TileMenu';
import { GROUP_ICONS } from './groupIcons';

/**
 * Плитка роли — раскрывающаяся, как у баннеров и объявлений. В раскрытии
 * показываются выданные права ТОЛЬКО ДЛЯ ЧТЕНИЯ, сгруппированные так же,
 * как в реестре Orchid; менять их можно через «Редактировать» в трёх точках.
 *
 * Права подгружаются при первом раскрытии, а не вместе со списком: у списка
 * ролей их нет, а тянуть все права всех ролей ради свёрнутых плиток незачем.
 */
export function RoleTile({
  role,
  catalog,
  canEdit,
  expanded,
  onToggleExpand,
  onEditRequest,
  onDeleted,
}: {
  role: RoleRow;
  catalog: PermissionGroup[] | null;
  canEdit: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  onEditRequest: () => void;
  onDeleted: () => void;
}) {
  const [granted, setGranted] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const { confirm, dialog } = useConfirmDialog();

  useEffect(() => {
    if (!expanded || granted !== null) return;
    let cancelled = false;
    fetchRole(role.id)
      .then((detail) => {
        if (!cancelled) setGranted(detail.permissions);
      })
      .catch(() => {
        if (!cancelled) showToast('Не удалось загрузить права роли.');
      });
    return () => {
      cancelled = true;
    };
  }, [expanded, granted, role.id]);

  const handleDelete = async () => {
    const ok = await confirm(`Удалить роль «${role.name}»? Это действие необратимо.`, {
      confirmLabel: 'Удалить',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await deleteRole(role.id);
      showToast('Роль удалена');
      onDeleted();
    } catch {
      showToast('Не удалось удалить роль.');
      setBusy(false);
    }
  };

  // Показываем только те группы, где роли что-то выдано — пустые разделы
  // в режиме просмотра лишний шум.
  const grantedGroups = (catalog ?? [])
    .map((group) => ({
      ...group,
      permissions: group.permissions.filter((p) => granted?.includes(p.slug)),
    }))
    .filter((group) => group.permissions.length > 0);

  return (
    <div className={`catalog-tile${expanded ? ' catalog-tile--expanded' : ''}`}>
      <div className="catalog-tile__main">
        <div className="catalog-tile__icon">
          <Icon name="lock" size={18} />
        </div>
        <button type="button" className="catalog-tile__body catalog-tile__body--clickable" onClick={onToggleExpand}>
          <div className="catalog-tile__name">
            {role.name}
            {/* «Доступно прав: N» — форма не зависит от числа, склонять нечего. */}
            <span className="badge badge--neutral">Доступно прав: {role.permissionsCount}</span>
          </div>
          <div className="catalog-tile__category">{role.slug}</div>
        </button>

        {canEdit && <TileMenu disabled={busy} onEdit={onEditRequest} onDelete={handleDelete} />}

        <button
          type="button"
          className="catalog-tile__expand"
          onClick={onToggleExpand}
          aria-expanded={expanded}
          aria-label={expanded ? `Скрыть «${role.name}»` : `Показать «${role.name}»`}
        >
          <Icon name="chevronDown" size={16} />
        </button>
      </div>

      {expanded && (
        <div className="catalog-tile__reports">
          {granted === null || catalog === null ? (
            <div className="panel-loading">
              <Spinner />
            </div>
          ) : grantedGroups.length === 0 ? (
            <p className="cell-muted">Роли не выдано ни одного права.</p>
          ) : (
            <div className="role-view">
              {grantedGroups.map((group) => (
                <section className="role-view__group" key={group.title}>
                  <header className="role-view__head">
                    <span className="role-view__icon">
                      <Icon name={GROUP_ICONS[group.title] ?? 'lock'} size={15} />
                    </span>
                    <span className="role-view__title">{group.title}</span>
                  </header>
                  <ul className="role-view__list">
                    {group.permissions.map((permission) => (
                      <li key={permission.slug}>
                        <Icon name="check" size={13} />
                        {permission.description}
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </div>
      )}

      {dialog}
    </div>
  );
}
